import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createApiApp, UserKeys } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import { ApigeeApp } from '../../../../types';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { Controller, useForm } from 'react-hook-form';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  AdministrationRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';

interface AddApiAppDialogProps {
  open: boolean;
  handleClose: () => void;
  changeLogRoles: string[];
}

export default function AddApiAppDialog({
  open,
  handleClose,
  changeLogRoles,
}: AddApiAppDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();

  const {
    handleSubmit,
    control,
    reset,
    getValues,
    formState: { errors, isDirty },
  } = useForm<Partial<ApigeeApp>>({
    defaultValues: {
      name: '',
      description: '',
      developer: '',
      appKey: '',
    },
  });

  const onSubmit = (data: Partial<ApigeeApp>) => {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        AdministrationRoles.CreateApiKeys.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (data: Partial<ApigeeApp>) =>
      createApiApp(data, changeLogMessage),
    onSuccess: (_returnedData, data) => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: 'App added successfully!',
      });

      queryClient.setQueryData<Partial<ApigeeApp>[]>(
        [UserKeys.ApigeeApps],
        (apiApps) => {
          data.lastModifiedTime = Date.now();
          if (apiApps) {
            return [...apiApps, data];
          } else {
            return [data];
          }
        }
      );

      setChangeLogMessage('');
      handleClose();
      reset();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          'There was an issue creating the api app. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(analytics.administration.api.create_api_app, {
        module: ModuleList.modules.administration.name,
        section: ModuleList.modules.administration.sections.api,
      });
      setOpenChangeLogDialog(false);
    },
  });

  return (
    <SharedDialog
      open={open}
      handleClose={() => {
        reset();
        handleClose();
      }}
      title="Create API App"
      dialogContent={
        <Stack gap={1}>
          <Controller
            name={'name'}
            control={control}
            rules={{
              required: 'Name is required.',
              minLength: { value: 1, message: 'Name is required.' },
            }}
            render={({ field }) => (
              <TextField
                inputProps={{
                  id: 'name',
                }}
                InputLabelProps={{
                  htmlFor: 'name',
                }}
                label="App Name"
                fullWidth
                autoComplete="off"
                error={!!errors.name}
                helperText={errors?.name?.message || ' '}
                required
                autoFocus
                {...field}
              />
            )}
          />
          <Controller
            name={'description'}
            control={control}
            rules={{
              maxLength: {
                value: 200,
                message: 'Description cannot exceed 200 characters.',
              },
            }}
            render={({ field }) => (
              <TextField
                inputProps={{
                  id: 'description',
                  maxLength: 200,
                }}
                InputLabelProps={{
                  htmlFor: 'description',
                }}
                label="Description"
                fullWidth
                autoComplete="off"
                error={!!errors.description}
                helperText={
                  errors?.description?.message ||
                  `${field?.value?.length || 0}/200`
                }
                multiline
                rows={3}
                {...field}
              />
            )}
          />
          <Controller
            name={'developer'}
            control={control}
            rules={{
              required: 'Email is required.',
              pattern: {
                value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                message: 'Invalid email address.',
              },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                inputProps={{
                  id: 'developeremail',
                }}
                InputLabelProps={{
                  htmlFor: 'developeremail',
                }}
                label="Developer Email"
                fullWidth
                autoComplete="off"
                error={!!errors.developer}
                helperText={errors?.developer?.message || ' '}
                required
                {...field}
              />
            )}
          />
          <Controller
            name={'appKey'}
            control={control}
            rules={{
              required: 'App Key is required.',
              minLength: { value: 1, message: 'App Key is required.' },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                inputProps={{
                  id: 'appKey',
                }}
                InputLabelProps={{
                  htmlFor: 'appKey',
                }}
                label="App Key"
                fullWidth
                autoComplete="off"
                required
                {...field}
              />
            )}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => {
              mutate(getValues());
            }}
            mutateLoading={isPending}
          />
        </Stack>
      }
      primaryButton={{
        loading: isPending,
        action: handleSubmit(onSubmit),
        disabled: isPending || !isDirty,
        title: 'Add App',
      }}
    />
  );
}
